/**
 * PupRing Custom JavaScript - Bundle Builder Together Module
 * ==========================================================
 * Buy 3 for $99 Bundle Builder - Unified Customization (Together Mode)
 *
 * Features:
 * - Unified customization form for all 3 products
 * - Synced variant selection across all products
 * - Common pet name input (when applicable)
 * - Shared photo upload (when applicable)
 * - Thumbnail preview sync
 *
 * Dependencies:
 * - custom-bundle-state.js (BundleStateManager)
 * - custom-bundle-ui.js (BundleUIManager)
 * - custom-bundle-photo.js (BundlePhotoUploader)
 */

/**
 * BundleTogetherManager - Handles unified customization (Together mode)
 *
 * Responsibilities:
 * - Renders unified customization form
 * - Syncs variant selection across all 3 products
 * - Updates thumbnails when variant changes
 * - Handles pet name input with validation
 * - Photo upload integration
 * - Validates completeness before continuing
 */
class BundleTogetherManager {
  constructor(stateManager, uiManager, productsData) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;
    this.productsData = productsData;

    // Container element
    this.container = null;

    // Photo uploader instance
    this.photoUploader = null;

    // Bound handlers for cleanup
    this.boundVariantHandler = null;
    this.boundPetNameHandler = null;
    this.boundContinueHandler = null;
    this.boundBackHandler = null;
    this.boundSwitchModeHandler = null;
    this.boundThumbnailClickHandler = null;

    // BUG-034 FIX: Cache swatch config
    this.swatchConfig = null;

    // Preview modal reference for handler cleanup
    this.previewModal = null;
  }

  // ═══════════════════════════════════════════════════════════════
  // BUG-034 FIX: SWATCH HELPER METHODS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Get variant image URL for a color option value
   * BUG-032 FIX: Use URL template from Liquid - works for ANY variant value
   * @param {string} optionName - Option name (e.g., "Material")
   * @param {string} value - Option value (e.g., "Gold")
   * @returns {string} Image URL
   */
  getVariantImageForOption(optionName, value) {
    const config = BundleUIManager.getSwatchConfig();

    // Convert value to handle (e.g., "18k Rose Gold Plated" → "18k-rose-gold-plated")
    const handle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    // Generate URL from template (works for ANY variant value)
    if (config.swatchUrlTemplate) {
      return config.swatchUrlTemplate.replace('{HANDLE}', handle);
    }

    // Fallback to variant image only if template not available
    if (this.productsData) {
      for (const productData of this.productsData) {
        if (!productData) continue;

        const optIndex = productData.options.findIndex(o => {
          const name = typeof o === 'string' ? o : o.name;
          return name.toLowerCase() === optionName.toLowerCase();
        });

        if (optIndex === -1) continue;

        const variant = productData.variants.find(v => v[`option${optIndex + 1}`] === value);
        if (variant?.featured_image?.src) {
          return variant.featured_image.src.replace(/\.([^.]+)$/, '_128x128_crop_center.$1');
        }
      }
    }
    return '';
  }

  /**
   * Initialize the together step
   */
  init() {
    // BUG-113 FIX: Clean up any previous state before reinitializing
    // Without this, event handlers accumulate on the container element
    // when user navigates away and returns to Together mode
    this.cleanup();

    this.container = document.getElementById('bundle-together-step');

    // BUG-024 FIX: Add error handling instead of silent failure
    if (!this.container) {
      console.error('[BundleTogetherManager] Container #bundle-together-step not found');
      // Try to show error in sidebar as fallback
      const sidebar = document.getElementById('bundle-builder-sidebar');
      const fallbackContainer = sidebar?.querySelector('.card-body');
      if (fallbackContainer) {
        fallbackContainer.innerHTML = `
          <div class="alert alert-danger">
            <p class="mb-2"><strong>Unable to load customization form</strong></p>
            <p class="mb-0">The page may not have loaded correctly. Please refresh and try again.</p>
          </div>
          <button class="btn btn-outline-secondary mt-3" onclick="window.BundleStateManager?.getInstance()?.goToStep('selection')">
            ← Go Back to Selection
          </button>
        `;
      }
      return;
    }

    this.syncDefaultSelections();
    this.render();
    this.bindEvents();

    // Initialize photo uploader after render
    this.initPhotoUploader();

    // BUG-042 FIX: Sync sidebar/mobile progress bar thumbnails on init
    // (matches Individual mode behavior at custom-bundle-individual.js:165)
    this.uiManager?.renderThumbnails?.(this.stateManager.get().products);
  }

  // ═══════════════════════════════════════════════════════════════
  // BUG-099: STEP NUMBER HELPER
  // ═══════════════════════════════════════════════════════════════

  /**
   * Calculate step numbers based on what sections are shown
   * @returns {Object} Step numbers for each section
   */
  getStepNumbers() {
    const state = this.stateManager.get();
    const steps = {};
    let stepNum = 1;

    // Check if we have variants to select
    const commonVariants = state.commonVariants || [];
    if (commonVariants.length > 0) {
      steps.material = stepNum++;
    }

    // Check if any product requires photo
    const needsPhoto = state.products.some(p => p.requirements?.requiresPhoto);
    if (needsPhoto) {
      steps.photo = stepNum++;
    }

    // Check if pet name is needed
    const petNameInfo = this.analyzePetNameRequirements();
    if (petNameInfo.show) {
      steps.petName = stepNum++;
    }

    return steps;
  }

  /**
   * Render the together step content
   */
  render() {
    const state = this.stateManager.get();
    const commonVariants = state.commonVariants || [];

    // Determine pet name requirements
    const petNameInfo = this.analyzePetNameRequirements();

    // BUG-099: Calculate step numbers
    const stepNumbers = this.getStepNumbers();

    this.container.innerHTML = `
      <div class="bundle-together-content">
        <h3 class="text-center" style="font-size: 28px; margin-top: 30px; margin-bottom: 25px;">Customize Your Bundle</h3>

        <!-- Synced Thumbnails -->
        <div class="bundle-together-thumbnails">
          ${this.renderThumbnails()}
        </div>

        <!-- Common Variant Selectors -->
        <div class="bundle-together-variants">
          ${this.renderVariantSelectors(commonVariants, stepNumbers)}
        </div>

        <!-- Photo Upload Section - BUG-099: Moved ABOVE pet name -->
        ${this.renderPhotoSection(stepNumbers)}

        <!-- Pet Name Section -->
        <div class="bundle-together-petname">
          ${this.renderPetNameSection(petNameInfo, stepNumbers)}
        </div>

        <!-- BUG-121 FIX: Product Descriptions moved AFTER customization (matches Individual mode) -->
        ${this.renderDescriptions()}

        <!-- Photo Guide Modal - BUG-099 -->
        ${this.renderPhotoGuideModal()}

        <!-- Navigation Buttons -->
        <div class="bundle-together-nav d-flex gap-3 mt-4">
          <button class="btn btn-outline-secondary flex-fill" data-bundle-together-back>
            ← Back
          </button>
          <button class="btn btn-primary flex-fill" data-bundle-together-continue>
            Continue to Extras →
          </button>
        </div>

        <!-- BUG-122: Switch Mode Link - Hidden on mobile (shown in sticky bar instead) -->
        <div class="bundle-together-switch-mode text-center mt-3">
          <a href="javascript:void(0)" class="text-muted small" data-bundle-switch-mode>
            Want different options for each piece? <u>Customize Individually</u>
          </a>
        </div>
      </div>
    `;
  }

  /**
   * Render synced thumbnails for all 3 products
   * @returns {string} HTML string
   */
  renderThumbnails() {
    const state = this.stateManager.get();

    return state.products.map((product, index) => {
      const image = product.selectedVariantImage || product.featuredImage;
      return `
        <div class="bundle-together-thumbnail" data-thumbnail-index="${index}">
          <img src="${this.uiManager.resizeImage(image, '100x100')}"
               alt="${this.escapeHtml(product.productTitle)}"
               loading="lazy">
        </div>
      `;
    }).join('');
  }

  /**
   * Render variant selectors for common options only
   * @param {Array} commonVariants - Array of common variant options
   * @param {Object} stepNumbers - Step numbers for each section (BUG-099)
   * @returns {string} HTML string
   */
  renderVariantSelectors(commonVariants, stepNumbers = {}) {
    if (!commonVariants || commonVariants.length === 0) {
      return '<p class="text-muted text-center">No common options to select</p>';
    }

    const state = this.stateManager.get();
    const selectedOptions = state.unifiedCustomization?.selectedOptions || {};

    // Get the first material/color option for the redesigned section
    const materialOption = commonVariants.find(opt =>
      BundleUIManager.isColorOption(opt.name) ||
      opt.name.toLowerCase().includes('material') ||
      opt.name.toLowerCase().includes('color')
    ) || commonVariants[0];

    // Wrap all variants in a styled section with title and border
    let html = `<div class="bundle-variant-section">`;

    // Add section title
    html += `<h5 class="bundle-variant-title">Step 1: Select the material</h5>`;

    // Add variants container
    html += `<div class="bundle-variant-content">`;

    html += commonVariants.map(option => {
      const selectedValue = selectedOptions[option.name];
      const optionName = this.escapeHtml(option.name);
      const isColorOpt = BundleUIManager.isColorOption(option.name);
      const optionHandle = option.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      if (isColorOpt) {
        // BUG-032 FIX: Render as card-style swatch matching product page
        return `
          <div class="product-option-wrapper bundle-variant-selector mb-4" data-handle="${optionHandle}">
            <ul class="variant-card-list variant-card-list--swatch list-unstyled mb-0"
                data-option-name="${optionName}">
              ${option.values.map(value => {
                const isSelected = value === selectedValue;
                const imgUrl = this.getVariantImageForOption(option.name, value);
                const valueHandle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                const safeValue = this.escapeHtml(value);

                return `
                  <li class="variant-card-item">
                    <input type="radio"
                           name="bundle-together-${optionHandle}"
                           id="bundle-together-${optionHandle}-${valueHandle}"
                           class="bundle-together-variant-input visually-hidden"
                           data-option-name="${optionName}"
                           data-option-value="${safeValue}"
                           ${isSelected ? 'checked' : ''}>
                    <label for="bundle-together-${optionHandle}-${valueHandle}"
                           class="variant-card-label"
                           data-bs-toggle="tooltip"
                           title="${safeValue}">
                      <img class="variant-card-img"
                           src="${imgUrl}"
                           alt="${safeValue}"
                           width="40"
                           height="40"
                           loading="eager"
                           onerror="this.src='data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 40 40%22><rect width=%2240%22 height=%2240%22 fill=%22%23f0f0f0%22 rx=%2220%22/></svg>'">
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
            <h4 class="variant-card-title title d-flex h6 mb-3">
              ${optionName}:
              <span class="text-muted ms-2">${this.escapeHtml(selectedValue)}</span>
            </h4>
          </div>
        `;
      } else {
        // BUG-032 FIX: Text options also use card style
        return `
          <div class="product-option-wrapper bundle-variant-selector mb-4" data-handle="${optionHandle}">
            <ul class="variant-card-list list-unstyled mb-0" data-option-name="${optionName}">
              ${option.values.map(value => {
                const isSelected = value === selectedValue;
                const valueHandle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                const safeValue = this.escapeHtml(value);

                return `
                  <li class="variant-card-item">
                    <input type="radio"
                           name="bundle-together-${optionHandle}"
                           id="bundle-together-${optionHandle}-${valueHandle}"
                           class="bundle-together-variant-input visually-hidden"
                           data-option-name="${optionName}"
                           data-option-value="${safeValue}"
                           ${isSelected ? 'checked' : ''}>
                    <label for="bundle-together-${optionHandle}-${valueHandle}"
                           class="variant-card-label">
                      ${safeValue}
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
            <h4 class="variant-card-title title d-flex h6 mb-3">
              ${optionName}:
              <span class="text-muted ms-2">${this.escapeHtml(selectedValue)}</span>
            </h4>
          </div>
        `;
      }
    }).join('');

    // Close variant content div
    html += `</div>`;

    // Close variant section div
    html += `</div>`;

    return html;
  }

  /**
   * Render product descriptions as collapsible accordions
   * BUG-088 FIX: Show product descriptions during customization
   * FEATURE: Accordion dropdowns with product name as title, collapsed by default
   * @returns {string} HTML string
   */
  renderDescriptions() {
    if (!this.productsData || this.productsData.length < 3) {
      return '';
    }

    const state = this.stateManager.get();

    // Build accordion items for each product with a description
    const accordionItems = state.products.map((product, index) => {
      const productData = this.productsData[index];
      if (!productData) return '';

      const desc = productData.body_html || productData.description || '';
      const trimmed = desc.trim();

      // Skip empty descriptions
      if (!trimmed || trimmed === '<p></p>' || trimmed === '<br>') {
        return '';
      }

      const collapseId = `bundle-together-collapse-${index}`;

      return `
        <div class="accordion-item border-0">
          <h2 class="accordion-header">
            <button class="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#${collapseId}"
                    aria-expanded="false"
                    aria-controls="${collapseId}">
              ${this.escapeHtml(product.productTitle)}
            </button>
          </h2>
          <div id="${collapseId}"
               class="accordion-collapse collapse"
               data-bs-parent="#bundle-together-descriptions">
            <div class="accordion-body">
              <div class="rte">${trimmed}</div>
            </div>
          </div>
        </div>
      `;
    }).filter(Boolean).join('');

    if (!accordionItems) return '';

    // BUG-122 FIX: Render accordion container with section header
    return `
      <div class="bundle-together-descriptions-section mb-4" style="margin-top: 20px;">
        <h5 style="font-size: 16px; margin-bottom: 15px;">Product Details</h5>
        <div class="accordion bundle-description-accordion" id="bundle-together-descriptions">
          ${accordionItems}
        </div>
      </div>
    `;
  }

  /**
   * Escape HTML for safe rendering in text and quoted attribute values
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Analyze pet name requirements across all products
   * @returns {Object} Pet name configuration
   */
  analyzePetNameRequirements() {
    const state = this.stateManager.get();
    const products = state.products;
    const namedProducts = products.filter(p => p.requirements?.requiresPetName);

    if (namedProducts.length === 0) {
      return { show: false, reason: 'not-required' };
    }

    // One shared set of names: a product with fewer pendants uses the first ones.
    // The count matches the state guard: highest pendant count among products that need a name.
    const inputCount = BundleStateManager.getTogetherNameCount(products);
    const charLimit = Math.min(...namedProducts.map(p => p.requirements?.charLimit || 8));
    const noCaps = namedProducts.some(p => p.requirements?.noCaps);

    return {
      show: true,
      inputCount,
      charLimit,
      noCaps
    };
  }

  /**
   * Render pet name input section
   * @param {Object} petNameInfo - Pet name configuration
   * @param {Object} stepNumbers - Step numbers for each section (BUG-099)
   * @returns {string} HTML string
   */
  renderPetNameSection(petNameInfo, stepNumbers = {}) {
    if (!petNameInfo.show) {
      return '';
    }

    const state = this.stateManager.get();
    const petNames = state.unifiedCustomization?.petNames || [];

    // BUG-099: Calculate step label
    const stepNum = stepNumbers.petName || '';
    const stepLabel = stepNum ? `<span class="bundle-step-number">Step ${stepNum}:</span> ` : '';

    const inputs = Array(petNameInfo.inputCount).fill(0).map((_, i) => {
      const value = petNames[i] || '';
      const label = petNameInfo.inputCount > 1 ? `Pendant ${i + 1}` : "Your Pet's Name";

      return `
        <div class="bundle-petname-input-group mb-3">
          <label class="form-label">${label}</label>
          <div class="input-group">
            <input type="text"
                   class="form-control bundle-petname-input"
                   data-petname-index="${i}"
                   maxlength="${petNameInfo.charLimit}"
                   placeholder="Enter name"
                   value="${this.escapeHtml(value)}">
            <span class="input-group-text bundle-petname-counter">
              ${value.length}/${petNameInfo.charLimit}
            </span>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="mb-4">
        <h5 class="mb-3">${stepLabel}Enter your pet's name${petNameInfo.inputCount > 1 ? 's' : ''} <span class="text-danger">*</span></h5>
        ${inputs}
      </div>
    `;
  }

  /**
   * Render photo upload section
   * @param {Object} stepNumbers - Step numbers for each section (BUG-099)
   * @returns {string} HTML string
   */
  renderPhotoSection(stepNumbers = {}) {
    const state = this.stateManager.get();

    // Check if any product requires photo
    const needsPhoto = state.products.some(p => p.requirements?.requiresPhoto);

    if (!needsPhoto) {
      return '';
    }

    // BUG-099: Calculate step label
    const stepNum = stepNumbers.photo || '';
    const stepLabel = stepNum ? `<span class="bundle-step-number">Step ${stepNum}:</span> ` : '';

    return `
      <div class="bundle-together-photo">
        <h5 style="margin-bottom: 15px;">${stepLabel}Add your pet's picture <span class="text-danger">*</span></h5>

        <div id="bundle-photo-container-together"></div>

        <!-- Photo Guide Button - moved under upload prompt -->
        <button type="button" class="btn btn-link p-0 bundle-photo-guide-btn mt-3" style="margin-bottom: 12px;"
                data-bs-toggle="modal" data-bs-target="#bundle-photo-guide-modal">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          Photo Guide
        </button>
      </div>
    `;
  }

  /**
   * Render Photo Guide modal
   * BUG-099: Added for Together mode
   * @returns {string} HTML string
   */
  renderPhotoGuideModal() {
    // Universal photo guide image (from snippets/product-block-photo-guide.liquid)
    const photoGuideUrl = 'https://cdn.shopify.com/s/files/1/0369/9854/5539/files/PHOTO_GUIDE_FINAL.jpg?v=1730942607';

    return `
      <div class="modal fade" id="bundle-photo-guide-modal" tabindex="-1" aria-labelledby="bundlePhotoGuideModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 450px;">
          <div class="modal-content" style="border-radius: 20px; overflow: hidden;">
            <div class="modal-header border-0 pb-0">
              <h5 class="modal-title fw-bold" id="bundlePhotoGuideModalLabel">PHOTO GUIDE</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body pt-2">
              <img src="${photoGuideUrl}" class="img-fluid rounded" alt="Photo guide showing best practices for pet photos" loading="lazy">
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn btn-outline-dark rounded-pill w-100" data-bs-dismiss="modal">
                Got it!
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Initialize photo uploader widget
   */
  async initPhotoUploader() {
    const state = this.stateManager.get();
    const needsPhoto = state.products.some(p => p.requirements?.requiresPhoto);

    if (!needsPhoto) return;

    const container = document.getElementById('bundle-photo-container-together');
    if (!container) return;

    // Create photo uploader instance if needed
    if (!this.photoUploader) {
      this.photoUploader = new BundlePhotoUploader(this.stateManager);
    }

    // Get existing photo URL from state
    const existingUrl = state.unifiedCustomization?.photoUrl || null;

    // Initialize uploader
    await this.photoUploader.initForTogether(container, existingUrl, (photoUrl) => {
      this.handlePhotoChange(photoUrl);
    });
  }

  /**
   * Handle photo upload/change
   * @param {string|null} photoUrl - The uploaded photo URL or null if removed
   */
  handlePhotoChange(photoUrl) {
    const state = this.stateManager.get();
    const previousPhotoUrl = state.unifiedCustomization?.photoUrl || null;

    // Save to unified customization state
    this.stateManager.set({
      unifiedCustomization: {
        ...state.unifiedCustomization,
        photoUrl
      }
    });
    this.syncCustomizationToProducts(photoUrl ? null : previousPhotoUrl);
  }

  /**
   * Copy the shared Together names and photo into each product's customization,
   * which the extras, summary and checkout steps read.
   * Until a name or photo is entered here, the values from quick view or
   * Individual mode are kept.
   * @param {string|null} removedPhotoUrl - Together photo just removed, cleared from products that carry it
   */
  syncCustomizationToProducts(removedPhotoUrl = null) {
    const state = this.stateManager.get();
    if (state.customizationMode !== 'together') return;

    const unified = state.unifiedCustomization || {};
    const petNames = unified.petNames || [];
    const hasTogetherNames = petNames.length > 0;
    const photoUrl = unified.photoUrl || null;

    const products = state.products.map(product => {
      const customization = { ...product.customization };

      if (hasTogetherNames) {
        customization.petNames = product.requirements?.requiresPetName
          ? Array.from({ length: product.pendantCount || 1 }, (_, i) => petNames[i] || '')
          : [];
      }

      if (photoUrl) {
        customization.photoUrl = product.requirements?.requiresPhoto ? photoUrl : null;
      } else if (removedPhotoUrl && customization.photoUrl === removedPhotoUrl) {
        customization.photoUrl = null;
      }

      return { ...product, customization };
    });

    this.stateManager.set({ products });
  }

  /**
   * Bind event handlers
   */
  bindEvents() {
    // BUG-115 FIX: Cleanup existing handlers first (matches Individual mode pattern)
    // This prevents handler accumulation when user navigates away and returns
    // The cleanup() call in init() handles the container-level cleanup,
    // but this provides double protection and matches the Individual mode pattern
    this.cleanup();

    // BUG-032 FIX: Variant selection with new card-style selectors
    this.boundVariantHandler = (e) => {
      const cardLabel = e.target.closest('.variant-card-label');
      if (cardLabel && !cardLabel.classList.contains('disabled')) {
        const input = cardLabel.previousElementSibling;
        if (input && input.classList.contains('bundle-together-variant-input')) {
          // The radio only changes once state accepts the selection
          e.preventDefault();
          const optionName = input.dataset.optionName;
          const optionValue = input.dataset.optionValue;

          if (!this.handleVariantChange(optionName, optionValue)) return;
          input.checked = true;

          // Update the title span with selected value
          const wrapper = cardLabel.closest('.product-option-wrapper');
          if (wrapper) {
            const titleSpan = wrapper.querySelector('.variant-card-title span.text-muted');
            if (titleSpan) {
              titleSpan.textContent = optionValue;
            }
          }
        }
      }
    };
    this.container.addEventListener('click', this.boundVariantHandler);

    // Pet name input
    this.boundPetNameHandler = (e) => {
      if (e.target.classList.contains('bundle-petname-input')) {
        const index = parseInt(e.target.dataset.petnameIndex, 10);
        this.handlePetNameInput(e.target.value, index);
      }
    };
    this.container.addEventListener('input', this.boundPetNameHandler);

    // Continue button
    this.boundContinueHandler = (e) => {
      if (e.target.closest('[data-bundle-together-continue]')) {
        this.handleContinue();
      }
    };
    this.container.addEventListener('click', this.boundContinueHandler);

    // Back button
    this.boundBackHandler = (e) => {
      if (e.target.closest('[data-bundle-together-back]')) {
        this.stateManager.goBack();
      }
    };
    this.container.addEventListener('click', this.boundBackHandler);

    // Switch mode link
    this.boundSwitchModeHandler = (e) => {
      if (e.target.closest('[data-bundle-switch-mode]')) {
        e.preventDefault();
        this.handleSwitchMode();
      }
    };
    this.container.addEventListener('click', this.boundSwitchModeHandler);

    // Thumbnail click for image preview modal
    this.boundThumbnailClickHandler = (e) => {
      const thumbnail = e.target.closest('.bundle-together-thumbnail');
      if (thumbnail) {
        const index = parseInt(thumbnail.dataset.thumbnailIndex, 10);
        this.showProductImagePreview(index);
      }
    };
    this.container.addEventListener('click', this.boundThumbnailClickHandler);
  }

  /**
   * Handle variant option change
   * BUG-116 FIX: Uses valueMapping to translate normalized display values to actual product-specific values
   *
   * @param {string} optionName - Option name (e.g., 'Material')
   * @param {string} displayValue - Selected display value (e.g., 'Silver' - the normalized value)
   * @returns {boolean} True when every product accepted the selection
   */
  handleVariantChange(optionName, displayValue) {
    // BUG-115 FIX: Validate productsData exists BEFORE any processing
    if (!this.productsData || this.productsData.length < 3) {
      console.error('[BundleTogetherManager] handleVariantChange: productsData invalid', {
        hasData: !!this.productsData,
        length: this.productsData?.length ?? 0
      });
      this.uiManager?.showToast?.('Product data not loaded. Please go back and try again.', 'error');
      return false;
    }

    const state = this.stateManager.get();

    // Update unified state with DISPLAY value (what user sees/clicked)
    const newSelectedOptions = {
      ...state.unifiedCustomization?.selectedOptions,
      [optionName]: displayValue
    };

    // BUG-053 FIX: Pre-check ALL products have this variant available
    const { products, unavailable } = this.resolveProductsForSelections(newSelectedOptions);
    if (unavailable.length > 0) {
      this.uiManager.showToast(
        `Variant sold out for: ${unavailable.join(', ')}`,
        'error'
      );
      return false; // Don't update ANY products
    }

    // Save updated state
    this.stateManager.set({
      products,
      unifiedCustomization: {
        ...state.unifiedCustomization,
        selectedOptions: newSelectedOptions  // Store DISPLAY values for UI consistency
      }
    });

    // Pendant counts may have changed: refresh the name inputs and per-product names
    const petNameContainer = this.container.querySelector('.bundle-together-petname');
    if (petNameContainer) {
      petNameContainer.innerHTML = this.renderPetNameSection(this.analyzePetNameRequirements(), this.getStepNumbers());
    }
    this.syncCustomizationToProducts();

    // Update thumbnails in Together step
    this.updateThumbnails();

    // Sync progress bar thumbnails (sidebar and mobile bar)
    if (this.uiManager && typeof this.uiManager.renderThumbnails === 'function') {
      this.uiManager.renderThumbnails(this.stateManager.get().products);
    }

    return true;
  }

  /**
   * Resolve every product's variant for the unified (display) selections.
   * Starts from the product's current variant so options outside the unified set
   * (pendant count, size) are kept; BUG-116 valueMapping translates each display
   * value to the product's own value. Falls back to a variant matching only the
   * unified options when the product has no such combination.
   * @param {Object} selections - Display values keyed by common option name
   * @returns {{ products: Array, unavailable: Array<string> }} Updated products and titles that cannot take the selection
   */
  resolveProductsForSelections(selections) {
    const state = this.stateManager.get();
    const commonVariants = state.commonVariants || [];
    const unavailable = [];

    const products = state.products.map((product, index) => {
      const productData = this.productsData?.[index];
      if (!productData) return product;

      const overrides = {};
      Object.entries(selections).forEach(([name, displayValue]) => {
        const commonOpt = commonVariants.find(cv => cv.name.toLowerCase() === name.toLowerCase());
        overrides[name] = commonOpt?.valueMapping?.[displayValue]?.[index] ?? displayValue;
      });

      const currentVariant = productData.variants.find(v => v.id === product.selectedVariantId);
      const exactVariant = currentVariant
        ? this.findMatchingVariant(productData, { ...this.getVariantOptions(productData, currentVariant), ...this.keyByProductOptions(productData, overrides) })
        : null;
      const variant = exactVariant || this.findMatchingVariant(productData, overrides);

      if (!variant || !variant.available) {
        unavailable.push(product.productTitle);
        return product;
      }

      return {
        ...product,
        selectedVariantId: variant.id,
        selectedVariantTitle: variant.title,
        selectedVariantImage: variant.featured_image?.src || product.featuredImage,
        selectedOptions: this.getVariantOptions(productData, variant),  // ACTUAL options for this product (for checkout)
        pendantCount: BundleStateManager.parseProductRequirements(productData, variant).pendantCount,
        pendantSize: this.getPendantSize(productData, variant)
      };
    });

    return { products, unavailable };
  }

  /**
   * Choose the unified selection shown when the step opens and write it to state,
   * so the checked options always match every product's variant.
   * Prefers a saved selection, then the value every product's current variant
   * already shares (choices made in quick view), then the first value every
   * product can take.
   */
  syncDefaultSelections() {
    const state = this.stateManager.get();
    const commonVariants = state.commonVariants || [];
    if (!this.productsData || this.productsData.length < 3) return;

    const saved = state.unifiedCustomization?.selectedOptions || {};
    let selections = {};

    commonVariants.forEach(option => {
      const preferred = [saved[option.name], this.getSharedCurrentValue(option)]
        .filter(value => option.values.includes(value));
      const candidates = [...preferred, ...option.values];
      const value = candidates.find(candidate =>
        this.resolveProductsForSelections({ ...selections, [option.name]: candidate }).unavailable.length === 0
      );
      if (value !== undefined) {
        selections = { ...selections, [option.name]: value };
      }
    });

    const { products } = this.resolveProductsForSelections(selections);
    this.stateManager.set({
      products,
      unifiedCustomization: {
        ...state.unifiedCustomization,
        selectedOptions: selections
      }
    });
  }

  /**
   * Display value of a common option that every product's current variant already has
   * @param {Object} option - Common option ({ name, values, valueMapping })
   * @returns {string|null} Shared display value, or null when products differ
   */
  getSharedCurrentValue(option) {
    const products = this.stateManager.get().products;

    return option.values.find(displayValue => products.every((product, index) => {
      const productData = this.productsData?.[index];
      const variant = productData?.variants.find(v => v.id === product.selectedVariantId);
      if (!variant) return false;

      const current = this.getVariantOptions(productData, variant);
      const optionKey = Object.keys(current).find(key => key.toLowerCase() === option.name.toLowerCase());
      const expected = option.valueMapping?.[displayValue]?.[index] ?? displayValue;
      return optionKey !== undefined && current[optionKey] === expected;
    })) || null;
  }

  /**
   * Option values of a variant keyed by the product's option names
   * @param {Object} productData - Full product data from Shopify
   * @param {Object} variant - Variant object
   * @returns {Object} e.g. { Material: 'Gold', Pendants: '2 Pendants' }
   */
  getVariantOptions(productData, variant) {
    const options = {};
    productData.options.forEach((o, i) => {
      options[typeof o === 'string' ? o : o.name] = variant[`option${i + 1}`];
    });
    return options;
  }

  /**
   * Re-key selections by the product's own option names (case-insensitive match)
   * so they replace, rather than sit beside, the current variant's values
   * @param {Object} productData - Full product data from Shopify
   * @param {Object} selections - Values keyed by option name
   * @returns {Object} Values keyed by the product's option names
   */
  keyByProductOptions(productData, selections) {
    const keyed = {};
    Object.entries(selections).forEach(([name, value]) => {
      const option = productData.options.find(o => (typeof o === 'string' ? o : o.name).toLowerCase() === name.toLowerCase());
      if (option) keyed[typeof option === 'string' ? option : option.name] = value;
    });
    return keyed;
  }

  /**
   * Pendant size of a variant ('1.5 cm' or '2.0 cm'); same rules as Individual mode
   * @param {Object} productData - Full product data from Shopify
   * @param {Object} variant - Variant object
   * @returns {string} Pendant size
   */
  getPendantSize(productData, variant) {
    const values = [variant.title, variant.option1, variant.option2, variant.option3]
      .map(v => (v || '').toLowerCase());
    if (values.some(v => v.includes('2.0') || v.includes('large'))) {
      return '2.0 cm';
    }

    const tags = Array.isArray(productData.tags)
      ? productData.tags
      : String(productData.tags || '').split(',');
    if (tags.some(t => t.toLowerCase().includes('2.0') || t.toLowerCase().includes('large-pendant'))) {
      return '2.0 cm';
    }

    return '1.5 cm';
  }

  /**
   * Find variant matching selected options, preferring an available one
   * @param {Object} productData - Full product data from Shopify
   * @param {Object} selectedOptions - Selected options object
   * @returns {Object|null} Matching variant or null
   */
  findMatchingVariant(productData, selectedOptions) {
    const matches = productData.variants.filter(variant => {
      return Object.entries(selectedOptions).every(([name, value]) => {
        // Find option position for this name
        // BUG-018 FIX: Handle both string and object formats for product.options
        const optIndex = productData.options.findIndex(o => {
          const optName = typeof o === 'string' ? o : o.name;
          return optName.toLowerCase() === name.toLowerCase();
        });
        if (optIndex === -1) return true; // Option doesn't exist, skip

        return variant[`option${optIndex + 1}`] === value;
      });
    });
    return matches.find(variant => variant.available) || matches[0] || null;
  }

  /**
   * Update thumbnail images after variant change
   */
  updateThumbnails() {
    const state = this.stateManager.get();
    const thumbnails = this.container.querySelectorAll('.bundle-together-thumbnail img');

    state.products.forEach((product, index) => {
      const thumb = thumbnails[index];
      if (thumb) {
        const newSrc = this.uiManager.resizeImage(
          product.selectedVariantImage || product.featuredImage,
          '100x100'
        );

        // Crossfade effect
        thumb.style.opacity = '0.5';
        thumb.src = newSrc;
        setTimeout(() => {
          thumb.style.opacity = '1';
        }, 150);
      }
    });
  }

  /**
   * Handle pet name input
   * @param {string} value - Input value
   * @param {number} index - Pet name index (0-based)
   */
  handlePetNameInput(value, index) {
    const state = this.stateManager.get();
    const petNameInfo = this.analyzePetNameRequirements();

    // Transform value based on noCaps setting
    let transformedValue = value;
    if (petNameInfo.noCaps) {
      // Title case
      transformedValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    } else {
      // UPPERCASE
      transformedValue = value.toUpperCase();
    }

    // Update input value if transformed
    const input = this.container.querySelector(`[data-petname-index="${index}"]`);
    if (input && input.value !== transformedValue) {
      input.value = transformedValue;
    }

    // Update counter
    const group = input?.closest('.bundle-petname-input-group');
    const counter = group?.querySelector('.bundle-petname-counter');
    if (counter) {
      counter.textContent = `${transformedValue.length}/${petNameInfo.charLimit}`;
    }

    // Update state
    const petNames = [...(state.unifiedCustomization?.petNames || [])];
    petNames[index] = transformedValue;

    this.stateManager.set({
      unifiedCustomization: {
        ...state.unifiedCustomization,
        petNames
      }
    });
    this.syncCustomizationToProducts();
  }

  /**
   * Validate if we can continue to next step
   * @returns {Object} { valid: boolean, message: string }
   */
  canContinue() {
    const state = this.stateManager.get();

    // All products must have variant selected, and every shared option a value
    const selectedOptions = state.unifiedCustomization?.selectedOptions || {};
    if (!state.products.every(p => p.selectedVariantId) ||
        (state.commonVariants || []).some(option => !selectedOptions[option.name])) {
      return { valid: false, message: 'Please select options for your bundle' };
    }

    // Check pet name requirement
    const petNameInfo = this.analyzePetNameRequirements();
    if (petNameInfo.show) {
      const petNames = state.unifiedCustomization?.petNames || [];

      for (let i = 0; i < petNameInfo.inputCount; i++) {
        if (!petNames[i] || petNames[i].trim() === '') {
          return {
            valid: false,
            message: petNameInfo.inputCount > 1
              ? `Please enter a name for pendant ${i + 1}`
              : "Please enter your pet's name"
          };
        }
      }
    }

    // Photo validation - Strict mode: block if required photo missing
    const needsPhoto = state.products.some(p => p.requirements?.requiresPhoto);
    if (needsPhoto && !state.unifiedCustomization?.photoUrl) {
      // Scroll to photo section for user attention
      const photoSection = document.querySelector('.bundle-together-photo');
      if (photoSection) {
        photoSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return { valid: false, message: 'Please upload a photo for your bundle' };
    }

    return { valid: true };
  }

  /**
   * Handle continue button click
   * Phase 9: Added shake animation and input error highlighting on validation failure
   */
  handleContinue() {
    const validation = this.canContinue();

    if (!validation.valid) {
      this.uiManager.showToast(validation.message, 'warning');

      // Phase 9: Add shake animation to form container
      const formContainer = this.container.querySelector('.bundle-together-content');
      if (formContainer && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        formContainer.classList.add('bundle-shake');
        setTimeout(() => formContainer.classList.remove('bundle-shake'), 500);
      }

      // Phase 9: Highlight empty pet name inputs
      const state = this.stateManager.get();
      const petNameInputs = this.container.querySelectorAll('.bundle-petname-input');
      const petNames = state.unifiedCustomization?.petNames || [];
      petNameInputs.forEach((input, index) => {
        if (!petNames[index] || petNames[index].trim() === '') {
          input.classList.add('bundle-input-error');
          setTimeout(() => input.classList.remove('bundle-input-error'), 1200);
          // Focus first invalid input
          if (index === 0 || !petNames[index - 1]) {
            input.focus();
          }
        }
      });

      return;
    }

    // Extras, summary and checkout read each product's own customization
    this.syncCustomizationToProducts();

    // Navigate to upsells step; explain a refusal instead of doing nothing
    if (!this.stateManager.goToStep('upsells')) {
      const diagnosis = this.stateManager.diagnoseStepAccess?.('upsells');
      this.uiManager.showToast(diagnosis?.reason || 'Please try again', 'warning');
    }
  }

  /**
   * Handle switch to individual mode
   */
  handleSwitchMode() {
    // Preserve variant selections when switching
    this.stateManager.set({
      customizationMode: 'individual',
      unifiedCustomization: null
    });

    this.uiManager.showToast('Switched to individual customization', 'info');
    this.stateManager.goToStep('individual-1');
  }

  // ═══════════════════════════════════════════════════════════════
  // IMAGE PREVIEW MODAL
  // ═══════════════════════════════════════════════════════════════

  /**
   * Show product image preview modal
   * BUG-124 FIX: Added proper error handling instead of silent failures
   * @param {number} index - Product index (0-2)
   */
  showProductImagePreview(index) {
    const state = this.stateManager.get();
    const product = state.products[index];

    // ERROR CHECK 1: Product not in state
    if (!product) {
      console.error('[BundleTogetherManager] showProductImagePreview: Product not found at index', index);
      this.uiManager?.showToast?.('Unable to load product preview. Please try again.', 'error');
      return;
    }

    // ERROR CHECK 2: productsData not loaded
    if (!this.productsData || this.productsData.length === 0) {
      console.error('[BundleTogetherManager] showProductImagePreview: productsData not loaded');
      this.uiManager?.showToast?.('Product data still loading. Please wait and try again.', 'warning');
      return;
    }

    // ERROR CHECK 3: Product not found in productsData
    const productData = this.productsData.find(p => p.id === product.productId);
    if (!productData) {
      console.error('[BundleTogetherManager] showProductImagePreview: productData not found for', {
        productId: product.productId,
        productTitle: product.productTitle
      });
      this.uiManager?.showToast?.('Unable to load product details. Please refresh the page.', 'error');
      return;
    }

    // BUG-124-followup: Store productData for use by variant handlers
    this.currentPreviewProductData = productData;

    // Get modal (create if needed)
    let modal = document.getElementById('bundle-image-preview-modal');
    if (!modal) {
      this.createImagePreviewModal();
      modal = document.getElementById('bundle-image-preview-modal');
    }

    // Populate content
    const contentEl = modal.querySelector('[data-preview-content]');
    contentEl.innerHTML = this.renderImagePreviewContent(productData, product);

    // Show modal
    const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
    bsModal.show();

    // Init Splide + variant handlers after modal shown (DOM must be visible)
    modal.addEventListener('shown.bs.modal', () => {
      this.initPreviewGallery(modal);
      // BUG-124-followup: Initialize clickable swatch handlers
      this.initPreviewVariantHandlers(modal);
    }, { once: true });

    // BUG-124 FIX: Cleanup Splide on modal close to prevent memory leaks
    modal.addEventListener('hidden.bs.modal', () => {
      this.cleanupPreviewGallery();
      // BUG-124-followup: Clear stored productData
      this.currentPreviewProductData = null;
    }, { once: true });
  }

  /**
   * Create the image preview modal HTML
   */
  createImagePreviewModal() {
    const modalHtml = `
      <div class="modal fade" id="bundle-image-preview-modal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content">
            <div class="modal-header border-0">
              <h5 class="modal-title" data-preview-title></h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body" data-preview-content>
              <!-- Gallery populated dynamically -->
            </div>
          </div>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  /**
   * Render the image gallery content for the modal
   * BUG-124 FIX: Added read-only variant swatches below gallery
   * @param {Object} productData - Product data from Shopify
   * @param {Object} product - Product state object
   * @returns {string} HTML string
   */
  renderImagePreviewContent(productData, product) {
    const images = productData.images || [];

    // Handle empty images - show placeholder with swatches
    if (images.length === 0) {
      return `
        <h5 class="text-center mb-3">${this.escapeHtml(product.productTitle)}</h5>
        <div class="text-center py-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" class="text-muted mb-3">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <p class="text-muted">No images available for this product</p>
        </div>
        ${this.renderPreviewSwatches(productData, product)}
      `;
    }

    // Main gallery slides
    const mainSlides = images.map((img, i) => `
      <li class="splide__slide">
        <img src="${this.uiManager.resizeImage(img.src || img, '600x600')}"
             alt="${this.escapeHtml(productData.title)} image ${i + 1}"
             class="img-fluid rounded">
      </li>
    `).join('');

    // Thumbnail slides
    const thumbSlides = images.map((img, i) => `
      <li class="splide__slide">
        <img src="${this.uiManager.resizeImage(img.src || img, '80x80')}"
             alt="${this.escapeHtml(productData.title)} thumbnail ${i + 1}">
      </li>
    `).join('');

    return `
      <h5 class="text-center mb-3">${this.escapeHtml(product.productTitle)}</h5>

      <div id="bundle-preview-main" class="splide mb-3">
        <div class="splide__track">
          <ul class="splide__list">${mainSlides}</ul>
        </div>
      </div>

      ${images.length > 1 ? `
        <div id="bundle-preview-thumbs" class="splide">
          <div class="splide__track">
            <ul class="splide__list">${thumbSlides}</ul>
          </div>
        </div>
      ` : ''}

      ${this.renderPreviewSwatches(productData, product)}
    `;
  }

  /**
   * Render clickable variant swatches for image preview modal
   * BUG-124-followup: Changed from read-only divs to radio+label pattern for click handling
   * @param {Object} productData - Full product data from Shopify
   * @param {Object} product - Product state object with selected variant
   * @returns {string} HTML string
   */
  renderPreviewSwatches(productData, product) {
    // Skip if product has only default variant
    if (productData.variants.length === 1 && productData.variants[0].title === 'Default Title') {
      return '';
    }

    // Get the selected variant
    const selectedVariant = productData.variants.find(v => v.id === product.selectedVariantId)
                            || productData.variants[0];

    // Build swatches for color/material options only (skip size, pendants, etc.)
    const swatchHtml = productData.options.map((option, optionIndex) => {
      // Handle both string and object formats
      const optionName = typeof option === 'string' ? option : option.name;

      // Only show color/material swatches (not size, pendants, etc.)
      if (!BundleUIManager.isColorOption(optionName)) {
        return '';
      }

      const optionKey = `option${optionIndex + 1}`;
      const uniqueValues = [...new Set(productData.variants.map(v => v[optionKey]))];
      const selectedValue = selectedVariant[optionKey];

      return `
        <div class="bundle-preview-swatches mt-3 pt-3 border-top">
          <div class="d-flex align-items-center justify-content-center gap-2 flex-wrap">
            <span class="text-muted small">${this.escapeHtml(optionName)}:</span>
            ${uniqueValues.map(value => {
              const isSelected = value === selectedValue;
              const imgUrl = BundleUIManager.getSwatchImageUrl(productData, optionIndex, value);
              const isAvailable = productData.variants.some(v =>
                v[optionKey] === value && v.available
              );
              const valueSlug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
              const safeValue = this.escapeHtml(value);

              // BUG-124-followup: Use radio+label pattern like Quick View modal
              return `
                <span class="bundle-preview-swatch-item">
                  <input type="radio"
                         name="bundle-preview-option-${optionIndex}"
                         id="bundle-preview-${optionIndex}-${valueSlug}"
                         class="bundle-preview-swatch-input visually-hidden"
                         data-option-value="${safeValue}"
                         data-option-index="${optionIndex}"
                         ${isSelected ? 'checked' : ''}
                         ${!isAvailable ? 'disabled' : ''}>
                  <label for="bundle-preview-${optionIndex}-${valueSlug}"
                         class="bundle-preview-swatch ${!isAvailable ? 'is-soldout' : ''}"
                         data-bs-toggle="tooltip"
                         title="${safeValue}${!isAvailable ? ' (Sold Out)' : ''}">
                    <img src="${imgUrl}"
                         alt="${safeValue}"
                         width="32"
                         height="32"
                         loading="lazy"
                         onerror="this.src='data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 32 32%22><rect width=%2232%22 height=%2232%22 fill=%22%23f0f0f0%22 rx=%224%22/></svg>'">
                  </label>
                </span>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }).filter(Boolean).join('');

    return swatchHtml;
  }

  /**
   * Initialize Splide gallery in the preview modal
   * @param {HTMLElement} modal - The modal element
   */
  initPreviewGallery(modal) {
    const mainEl = modal.querySelector('#bundle-preview-main');
    const thumbsEl = modal.querySelector('#bundle-preview-thumbs');

    if (!mainEl) return;

    // Destroy previous instances
    if (this.previewMainSplide) {
      this.previewMainSplide.destroy();
      this.previewMainSplide = null;
    }
    if (this.previewThumbsSplide) {
      this.previewThumbsSplide.destroy();
      this.previewThumbsSplide = null;
    }

    // Initialize new galleries
    if (thumbsEl) {
      // BUG-124-followup: Increased from 60x60 to 80x80 to match Quick View modal
      this.previewThumbsSplide = new Splide(thumbsEl, {
        rewind: true,
        fixedWidth: 80,
        fixedHeight: 80,
        gap: 10,
        isNavigation: true,
        pagination: false,
        arrows: false
      });

      this.previewMainSplide = new Splide(mainEl, {
        type: 'fade',
        rewind: true,
        pagination: false,
        arrows: true,
        speed: 250
      });

      this.previewMainSplide.sync(this.previewThumbsSplide);
      this.previewMainSplide.mount();
      this.previewThumbsSplide.mount();
    } else {
      // Single image - no sync needed
      this.previewMainSplide = new Splide(mainEl, {
        type: 'fade',
        rewind: true,
        pagination: false,
        arrows: false,
        speed: 250
      });
      this.previewMainSplide.mount();
    }
  }

  /**
   * Cleanup Splide instances when preview modal closes
   * BUG-124 FIX: Prevents memory leaks from orphaned Splide instances
   */
  cleanupPreviewGallery() {
    if (this.previewMainSplide) {
      try {
        this.previewMainSplide.destroy();
      } catch (e) {
        // Splide may already be destroyed
      }
      this.previewMainSplide = null;
    }
    if (this.previewThumbsSplide) {
      try {
        this.previewThumbsSplide.destroy();
      } catch (e) {
        // Splide may already be destroyed
      }
      this.previewThumbsSplide = null;
    }
  }

  /**
   * Initialize variant swatch handlers for preview modal
   * BUG-124-followup: Allows clicking swatches to navigate to variant images
   * @param {HTMLElement} modal - The modal element
   */
  initPreviewVariantHandlers(modal) {
    const productData = this.currentPreviewProductData;
    if (!productData || !modal) return;

    // Store modal reference for cleanup
    this.previewModal = modal;

    // Remove existing listener if any (prevent accumulation)
    if (this._boundPreviewVariantHandler) {
      modal.removeEventListener('change', this._boundPreviewVariantHandler);
    }

    // Create bound handler for cleanup later
    this._boundPreviewVariantHandler = (e) => {
      if (!e.target.classList.contains('bundle-preview-swatch-input')) return;

      const optionIndex = parseInt(e.target.dataset.optionIndex, 10);
      const value = e.target.dataset.optionValue;

      // Find variant with this option value (prefer available, fallback to any)
      const matchingVariant = productData.variants.find(v =>
        v[`option${optionIndex + 1}`] === value && v.available
      ) || productData.variants.find(v => v[`option${optionIndex + 1}`] === value);

      // Navigate to variant's featured image if it has one
      if (matchingVariant?.featured_image && this.previewMainSplide) {
        const imageSrc = matchingVariant.featured_image.src;
        const imageFilename = imageSrc.split('?')[0].split('/').pop();

        const imageIndex = productData.images.findIndex(img =>
          (img.src || img).includes(imageFilename)
        );

        if (imageIndex >= 0) {
          this.previewMainSplide.go(imageIndex);
        }
      }
    };

    modal.addEventListener('change', this._boundPreviewVariantHandler);
  }

  /**
   * Cleanup event listeners
   */
  cleanup() {
    if (this.container) {
      if (this.boundVariantHandler) {
        this.container.removeEventListener('click', this.boundVariantHandler);
      }
      if (this.boundPetNameHandler) {
        this.container.removeEventListener('input', this.boundPetNameHandler);
      }
      if (this.boundContinueHandler) {
        this.container.removeEventListener('click', this.boundContinueHandler);
      }
      if (this.boundBackHandler) {
        this.container.removeEventListener('click', this.boundBackHandler);
      }
      if (this.boundSwitchModeHandler) {
        this.container.removeEventListener('click', this.boundSwitchModeHandler);
      }
      if (this.boundThumbnailClickHandler) {
        this.container.removeEventListener('click', this.boundThumbnailClickHandler);
      }
    }

    this.boundVariantHandler = null;
    this.boundPetNameHandler = null;
    this.boundContinueHandler = null;
    this.boundBackHandler = null;
    this.boundSwitchModeHandler = null;
    this.boundThumbnailClickHandler = null;

    // Cleanup preview gallery Splide instances
    if (this.previewMainSplide) {
      this.previewMainSplide.destroy();
      this.previewMainSplide = null;
    }
    if (this.previewThumbsSplide) {
      this.previewThumbsSplide.destroy();
      this.previewThumbsSplide = null;
    }

    // Cleanup preview modal variant handler
    if (this.previewModal && this._boundPreviewVariantHandler) {
      this.previewModal.removeEventListener('change', this._boundPreviewVariantHandler);
      this._boundPreviewVariantHandler = null;
      this.previewModal = null;
    }

    // Cleanup photo uploader
    if (this.photoUploader) {
      this.photoUploader.cleanup();
      this.photoUploader = null;
    }
  }
}

// Export for other modules
window.BundleTogetherManager = BundleTogetherManager;
